/**
 * Active-client context (multi-client plan).
 *
 * One tiny Zustand store + synchronous accessors so non-React modules
 * (docs-api's header injection, the client-scoped IDB wrapper) can read the
 * active client without a hook. Resolution happens in `ClientProvider`:
 *
 *   - client users:   clientId = their org id from token claims ("org").
 *     They never pick, never see other clients, and never send the header —
 *     the BFF scopes them server-side from their token.
 *   - facilitators:   clientId = an explicit pick, persisted per-tab in
 *     sessionStorage ("picked"). Every BFF call then carries X-Client-Id.
 *     A fresh tab has no pick and must choose via the ClientSelectGate.
 *     ("facilitators" here means DXC home users — any role; BFF scopes
 *     by resident org.)
 *   - seed mode:      a static stub client ("seed") so offline dev works.
 *
 * Switching clients is a full location.reload() (ClientSwitchModal): ~30
 * Zustand stores, module-scoped ETag maps and the query cache all hold
 * client data in memory — a reload resets every one of them by
 * construction, which per-store reset plumbing could only approximate.
 */

import { create } from "zustand";
import { isMultiClientEnabled } from "@/env";

export const ACTIVE_CLIENT_SESSION_KEY = "xaes-active-client";

/** Stub registry for seed/offline mode — lets the whole facilitator UX
 *  (gate, badge, switcher) run without a BFF. */
export const SEED_CLIENTS = [
  { id: "local", name: "Local Dev", status: "active" },
  { id: "demo-b", name: "Demo Client B", status: "active" },
] as const;

export type ActiveClientSource = "org" | "picked" | "seed" | "none";

interface ActiveClientState {
  /** Active client id, or null when unresolved / flag off. */
  clientId: string | null;
  /** Display name when known (facilitator pick / seed stub). */
  clientName: string | null;
  source: ActiveClientSource;
  /** True once resolution ran — hydration must not start before this. */
  resolved: boolean;
  resolve: (next: {
    clientId: string | null;
    clientName?: string | null;
    source: ActiveClientSource;
  }) => void;
}

export const useActiveClient = create<ActiveClientState>((set) => ({
  clientId: null,
  clientName: null,
  source: "none",
  resolved: false,
  resolve: ({ clientId, clientName = null, source }) =>
    set({ clientId, clientName, source, resolved: true }),
}));

/**
 * The active client id for CACHE KEYING — non-null for both client users
 * (org-derived) and facilitators (picked). Null when the flag is off or
 * resolution hasn't run.
 */
export function getActiveClientId(): string | null {
  if (!isMultiClientEnabled()) return null;
  return useActiveClient.getState().clientId;
}

/**
 * The client id to send as X-Client-Id — ONLY a facilitator's explicit
 * pick. Client users return null here (no header): their scoping is the
 * BFF's job, derived from the verified token, and the BFF hard-403s a
 * client caller who sends the header.
 */
export function actAsClientId(): string | null {
  if (!isMultiClientEnabled()) return null;
  const { clientId, source } = useActiveClient.getState();
  return source === "picked" ? clientId : null;
}

/** The per-tab persisted facilitator pick (raw; validation is the BFF's). */
export function storedClientPick(): string | null {
  try {
    return window.sessionStorage.getItem(ACTIVE_CLIENT_SESSION_KEY);
  } catch {
    return null;
  }
}

export function storeClientPick(clientId: string): void {
  try {
    window.sessionStorage.setItem(ACTIVE_CLIENT_SESSION_KEY, clientId);
  } catch {
    /* sessionStorage unavailable — the pick just won't survive a reload */
  }
}
